import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useBanStatus } from '@/hooks/useBanStatus';
import { useMetaTags } from '@/hooks/useMetaTags';
import PageHeader from '@/components/PageHeader';
import { Loader2, ArrowUpNarrowWide, Check, ImageIcon, Heart, MessageCircle, Ghost } from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { deleteMediaById, optimizeCloudinaryUrl } from '@/lib/utils';
import { useAlbumNavigation } from '@/hooks/useAlbumNavigation';
import { useAlbumContext } from '@/contexts/AlbumProvider';
import { safeLogRocketTrack } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeProvider';
import { useTranslation } from '@/hooks/useTranslation';
import { useSettings } from '@/hooks/useSettings';
import { useAlbumInteractions } from '@/contexts/AlbumInteractionsContext';
import { MediaGrid } from '@/components/media-grid';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import AlbumSlideshow from './album/components/AlbumSlideshow';
import { buildLoopedAssets, type MediaItem } from './album/utils';
import { DRAG_DISTANCE_THRESHOLD_PX, DRAG_VELOCITY_THRESHOLD } from './album/constants';
import { getSurfaceContrastColors, toSurfaceContrastVariables } from '@/lib/contrastUtils';

interface UserMedia {
  id: string;
  public_id: string;
  secure_url: string;
  inserted_at: string;
  created_at?: string;
  resource_type: 'image' | 'video';
  width: number;
  height: number;
  votes_count: number;
  comments_count: number;
  flagged: boolean;
  cdn_thumb?: string;
  cdn_display_s?: string;
  cdn_display_m?: string;
  cdn_original?: string;
  description?: string;
  uploaded_by: string;
}

type UserMediaVoteRow = {
  id: string;
  public_id?: string | null;
  secure_url: string;
  inserted_at: string;
  created_at?: string | null;
  resource_type?: string | null;
  width?: number | null;
  height?: number | null;
  votes_count?: number | null;
  comments_count?: number | null;
  flagged?: boolean | null;
  cdn_thumb?: string | null;
  cdn_display_s?: string | null;
  cdn_display_m?: string | null;
  cdn_original?: string | null;
  description?: string | null;
  uploaded_by: string;
};

type SortOption = 'votes' | 'recent' | 'comments';
const UUID_V4_LIKE_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const getDisplayName = (userData: {
  user_metadata?: Record<string, unknown> | null;
  email?: string | null;
}): string => {
  const userMeta = userData.user_metadata as {
    name?: string;
    full_name?: string;
  } | null;

  return userMeta?.name || userMeta?.full_name || userData.email?.split('@')[0] || 'Usuario';
};

const getAvatarUrl = (userData: {
  user_metadata?: Record<string, unknown> | null;
}): string => {
  const userMeta = userData.user_metadata as {
    picture?: string;
    avatar_url?: string;
  } | null;

  return userMeta?.picture || userMeta?.avatar_url || '';
};

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const Usuario = () => {
  const { user } = useAuth();
  const { isAdmin, isSuperadmin } = useAdminAuth();
  const { isBanned } = useBanStatus();
  const [searchParams] = useSearchParams();
  const targetUserId = searchParams.get('userId');
  const sortByQuery = searchParams.get('sortBy') as SortOption;
  const { getAlbumPath } = useAlbumNavigation();
  const { albumId, albumSlug } = useAlbumContext();
  const { tokens } = useTheme();
  const { t, currentLanguage } = useTranslation();
  const { getBooleanSetting, getSetting } = useSettings();
  const {
    registerMediaItems,
    applyOptimisticCommentDelta,
    applyOptimisticVoteState,
    getVotesCount,
    getCommentsCount,
  } = useAlbumInteractions();
  const isCommentsEnabled = getSetting('enable_comments') === 'true';
  const isLikesEnabled = getBooleanSetting('enable_likes', false);
  const isUploadsEnabled = getSetting('enable_uploads') === 'true';
  const isDownloadEnabled = getBooleanSetting('enable_single_image_download', false);
  const isDescriptionEnabled = getBooleanSetting('enable_single_image_description', false);
  const cardSurface = tokens.colors.card || tokens.colors.background || '0 0% 100%';
  const cardText = tokens.colors['card-foreground'] || tokens.colors.foreground;
  const emptyStateStyle = useMemo<React.CSSProperties>(
    () => toSurfaceContrastVariables(getSurfaceContrastColors(cardSurface, cardText)) as React.CSSProperties,
    [cardSurface, cardText],
  );

  useMetaTags({
    title: 'Mi Usuario',
    pathname: '/usuario',
  });

  const [userMedia, setUserMedia] = useState<UserMedia[]>([]);
  const [loading, setLoading] = useState(true);

  const getInitialSortOption = (): SortOption => {
    if (!sortByQuery) return 'recent';
    if (sortByQuery === 'votes' && isLikesEnabled) return 'votes';
    if (sortByQuery === 'comments' && isCommentsEnabled) return 'comments';
    return 'recent';
  };

  const [sortBy, setSortBy] = useState<SortOption>(getInitialSortOption());
  const [targetUserName, setTargetUserName] = useState<string>('');
  const [targetUserAvatar, setTargetUserAvatar] = useState<string>('');
  const [isTargetUserNotFound, setIsTargetUserNotFound] = useState(false);

  const [userVotes, setUserVotes] = useState<Set<string>>(new Set());
  const [userCommentedMediaIds, setUserCommentedMediaIds] = useState<Set<string>>(new Set());

  const [slideshowOpen, setSlideshowOpen] = useState(false);
  const [slideshowIndex, setSlideshowIndex] = useState(0);
  const [pos, setPos] = useState(1);
  const [noTransition, setNoTransition] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [isVotesOpen, setIsVotesOpen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeletingMedia, setIsDeletingMedia] = useState(false);
  const [isTogglingFlag, setIsTogglingFlag] = useState(false);
  const [pendingDeleteMediaId, setPendingDeleteMediaId] = useState<string | null>(null);

  const [touchStartY, setTouchStartY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  const lastYRef = useRef(0);
  const lastTimeRef = useRef(0);
  const velocityRef = useRef(0);
  const popClosingRef = useRef(false);
  const isOwnProfileView = !targetUserId || targetUserId === user?.id;

  useEffect(() => {
    const fetchUserMedia = async () => {
      const userId = targetUserId || user?.id;
      if (!userId) return;

      try {
        setLoading(true);

        if (targetUserId) {
          const normalizedTargetUserId = targetUserId.trim();
          if (!UUID_V4_LIKE_REGEX.test(normalizedTargetUserId)) {
            setTargetUserName('');
            setTargetUserAvatar('');
            setIsTargetUserNotFound(true);
            setUserMedia([]);
            setLoading(false);
            return;
          }

          const { data: userDataList, error: userError } = await supabase.rpc(
            'get_user_display_info',
            { user_ids: [normalizedTargetUserId] } as never,
          );

          if (userError) {
            Sentry.captureMessage(
              `Error fetching user data: ${userError instanceof Error ? userError.message : String(userError)} `,
              'error',
            );
            console.error('Error fetching user data:', userError);
            setTargetUserName('');
            setTargetUserAvatar('');
            setIsTargetUserNotFound(true);
            setUserMedia([]);
            setLoading(false);
            return;
          } else if (userDataList && userDataList.length > 0 && userDataList[0]?.id) {
            const userData = userDataList[0] as { display_name?: string | null; avatar_url?: string | null };
            setTargetUserName(userData.display_name || 'Usuario');
            setTargetUserAvatar(userData.avatar_url || '');
            setIsTargetUserNotFound(false);
          } else {
            setTargetUserName('');
            setTargetUserAvatar('');
            setIsTargetUserNotFound(true);
            setUserMedia([]);
            setLoading(false);
            return;
          }
        } else {
          setTargetUserName('');
          setTargetUserAvatar('');
          setIsTargetUserNotFound(false);
        }

        const validAlbumId = albumId && albumId.trim() !== '' ? albumId : null;
        const { data: allMedia, error } = await supabase.rpc(
          'get_media_with_votes_filtered',
          { p_album_id: validAlbumId, p_exclude_flagged: false } as never,
        );

        if (error) {
          Sentry.captureMessage(
            `Error fetching media with votes: ${error instanceof Error ? error.message : String(error)} `,
            'error',
          );
          console.error('Error fetching media with votes:', error);
          return;
        }

        const mediaRows = (allMedia || []) as UserMediaVoteRow[];
        const mediaWithVotes = mediaRows
          .filter((item) => item.uploaded_by === userId)
          .map((item) => ({
            id: item.id,
            public_id: item.public_id || item.secure_url?.split('/').pop()?.split('.')[0] || item.id,
            secure_url: item.secure_url,
            inserted_at: item.inserted_at,
            created_at: item.created_at || item.inserted_at,
            resource_type: item.resource_type === 'video' ? 'video' : 'image',
            width: item.width || 1,
            height: item.height || 1,
            votes_count: item.votes_count || 0,
            comments_count: item.comments_count || 0,
            flagged: item.flagged || false,
            cdn_thumb: item.cdn_thumb || optimizeCloudinaryUrl({ originalUrl: item.secure_url, width: 100 }),
            cdn_display_s: item.cdn_display_s || optimizeCloudinaryUrl({ originalUrl: item.secure_url, width: 600 }),
            cdn_display_m: item.cdn_display_m || optimizeCloudinaryUrl({ originalUrl: item.secure_url, width: 1200 }),
            cdn_original: item.cdn_original || item.secure_url,
            description: item.description || undefined,
            uploaded_by: item.uploaded_by,
          }));

        setUserMedia(mediaWithVotes);
        setLoading(false);

        if (!targetUserId && !isAdmin) {
          safeLogRocketTrack('Visitó su perfil', {
            user_id: user?.id,
            album_id: albumId,
            album_name: albumSlug,
          });

          safeLogRocketTrack('Total fotos del usuario', {
            count: mediaWithVotes.length,
            user_id: user?.id,
          });

          safeLogRocketTrack('Total likes del usuario', {
            count: mediaWithVotes.reduce((sum, m) => sum + (m.votes_count || 0), 0),
            user_id: user?.id,
          });
        }
      } catch (error) {
        Sentry.captureMessage(error instanceof Error ? error.message : String(error), 'error');
        console.error('Error:', error);
        setLoading(false);
      }
    };

    fetchUserMedia();
  }, [user?.id, targetUserId, isAdmin, albumId, albumSlug, user]);

  const canViewFlaggedMedia = isOwnProfileView || isAdmin || isSuperadmin;
  const visibleMediaForViewer = useMemo(
    () => (canViewFlaggedMedia ? userMedia : userMedia.filter((item) => !item.flagged)),
    [canViewFlaggedMedia, userMedia],
  );
  const canSortByVotes = useMemo(
    () =>
      isLikesEnabled &&
      visibleMediaForViewer.some((item) => getVotesCount(item.id, item.votes_count || 0) > 0),
    [isLikesEnabled, visibleMediaForViewer, getVotesCount],
  );
  const canSortByComments = useMemo(
    () =>
      isCommentsEnabled &&
      visibleMediaForViewer.some((item) => getCommentsCount(item.id, item.comments_count || 0) > 0),
    [isCommentsEnabled, visibleMediaForViewer, getCommentsCount],
  );
  const hasInteractionSortOptions = canSortByVotes || canSortByComments;

  const sortedMedia = useMemo(() => {
    const sorted = [...visibleMediaForViewer];
    if (sortBy === 'votes' && canSortByVotes) {
      return sorted.sort((a, b) => {
        const votesDiff = getVotesCount(b.id, b.votes_count || 0) - getVotesCount(a.id, a.votes_count || 0);
        if (votesDiff !== 0) return votesDiff;
        return new Date(b.inserted_at).getTime() - new Date(a.inserted_at).getTime();
      });
    }

    if (sortBy === 'comments' && canSortByComments) {
      return sorted.sort((a, b) => {
        const commentsDiff = getCommentsCount(b.id, b.comments_count || 0) - getCommentsCount(a.id, a.comments_count || 0);
        if (commentsDiff !== 0) return commentsDiff;
        return new Date(b.inserted_at).getTime() - new Date(a.inserted_at).getTime();
      });
    }

    return sorted.sort((a, b) => new Date(b.inserted_at).getTime() - new Date(a.inserted_at).getTime());
  }, [visibleMediaForViewer, sortBy, canSortByVotes, canSortByComments, getVotesCount, getCommentsCount]);

  useEffect(() => {
    if (sortBy === 'votes' && !canSortByVotes) {
      setSortBy('recent');
      return;
    }
    if (sortBy === 'comments' && !canSortByComments) {
      setSortBy('recent');
    }
  }, [sortBy, canSortByVotes, canSortByComments]);

  useEffect(() => {
    registerMediaItems(sortedMedia);
  }, [sortedMedia, registerMediaItems]);

  useEffect(() => {
    if (!user) {
      setUserVotes(new Set());
      setUserCommentedMediaIds(new Set());
      return;
    }

    let cancelled = false;

    const fetchCurrentUserInteractions = async () => {
      try {
        const { data: voteData, error: voteError } = await supabase
          .rpc('get_user_votes', { user_uuid: user.id });

        if (voteError) {
          throw voteError;
        }

        const votedIds = new Set<string>((voteData || []).map((vote: { media_id: string }) => vote.media_id));

        const mediaIds = userMedia.map((media) => media.id);
        const commentedIds = new Set<string>();

        if (mediaIds.length > 0) {
          const chunkSize = 200;
          for (let i = 0; i < mediaIds.length; i += chunkSize) {
            const chunk = mediaIds.slice(i, i + chunkSize);
            const { data: commentsData, error: commentsError } = await supabase
              .from('comments')
              .select('media_id')
              .eq('user_id', user.id)
              .in('media_id', chunk);

            if (commentsError) {
              throw commentsError;
            }

            (commentsData || []).forEach((row) => {
              if (row.media_id) commentedIds.add(row.media_id);
            });
          }
        }

        if (!cancelled) {
          setUserVotes(votedIds);
          setUserCommentedMediaIds(commentedIds);
        }
      } catch (error) {
        console.error('Error fetching user interactions:', error);
      }
    };

    void fetchCurrentUserInteractions();

    return () => {
      cancelled = true;
    };
  }, [user, userMedia]);

  const publicMedia = userMedia.filter((media) => !media.flagged);
  const totalVotes = publicMedia.reduce((sum, media) => sum + (media.votes_count || 0), 0);
  const totalComments = publicMedia.reduce((sum, media) => sum + (media.comments_count || 0), 0);

  const selfUserName = getDisplayName({
    user_metadata: (user?.user_metadata as Record<string, unknown> | null) || null,
    email: user?.email || null,
  });

  const selfUserAvatar = getAvatarUrl({
    user_metadata: (user?.user_metadata as Record<string, unknown> | null) || null,
  });

  const displayName = targetUserId ? targetUserName || 'Usuario' : selfUserName || 'Mi Usuario';
  const displayAvatar = targetUserId ? targetUserAvatar : selfUserAvatar;

  const pageTitle = '';
  const mediaWithUserInteractions = sortedMedia.map((item) => ({
    ...item,
    current_user_voted: userVotes.has(item.id),
    current_user_commented: userCommentedMediaIds.has(item.id),
  }));

  const usuarioSlideshowAssets: MediaItem[] = useMemo(() => {
    return sortedMedia.map((item) => ({
      id: item.id,
      public_id: item.public_id,
      secure_url: item.secure_url,
      resource_type: item.resource_type,
      format: '',
      width: item.width || 1,
      height: item.height || 1,
      created_at: item.created_at || item.inserted_at,
      uploaded_by: item.uploaded_by,
      user_name: displayName,
      user_avatar: displayAvatar || undefined,
      description: item.description,
      votes_count: item.votes_count || 0,
      comments_count: item.comments_count || 0,
      flagged: item.flagged || false,
      cdn_thumb: item.cdn_thumb,
      cdn_display_s: item.cdn_display_s,
      cdn_display_m: item.cdn_display_m,
      cdn_original: item.cdn_original || item.secure_url,
      pending: false,
    }));
  }, [sortedMedia, displayName, displayAvatar]);

  const loopedAssets = useMemo(
    () => buildLoopedAssets(usuarioSlideshowAssets),
    [usuarioSlideshowAssets]
  );
  const currentAsset = usuarioSlideshowAssets[slideshowIndex] || null;
  const canDeleteCurrentAsset = Boolean(
    user?.id &&
    isOwnProfileView &&
    currentAsset?.uploaded_by === user.id
  );
  const canToggleCurrentAsset = Boolean(
    user?.id &&
    canViewFlaggedMedia &&
    currentAsset?.uploaded_by
  );

  const statsItems = [
    {
      key: 'photos',
      value: publicMedia.length,
      icon: ImageIcon,
      visible: true,
    },
    {
      key: 'likes',
      value: totalVotes,
      icon: Heart,
      visible: isLikesEnabled,
    },
    {
      key: 'comments',
      value: totalComments,
      icon: MessageCircle,
      visible: isCommentsEnabled,
    },
  ].filter((item) => item.visible);

  const visibleStatsCount = 1 + (isLikesEnabled ? 1 : 0) + (isCommentsEnabled ? 1 : 0);
  const statsGridClass =
    visibleStatsCount === 3 ? 'grid-cols-3' : visibleStatsCount === 2 ? 'grid-cols-2' : 'grid-cols-1';

  const openSlideshow = (mediaId: string) => {
    const index = usuarioSlideshowAssets.findIndex((item) => item.id === mediaId);
    if (index === -1) return;

    setSlideshowIndex(index);
    setPos(index + 1);
    setSlideshowOpen(true);
    setIsTransitioning(false);
    setIsDescriptionExpanded(false);
  };

  const closeSlideshow = () => {
    if (!popClosingRef.current && slideshowOpen && window.history.state?.usuarioSlideshow) {
      window.history.back();
      return;
    }

    popClosingRef.current = false;
    setSlideshowOpen(false);
    setIsCommentsOpen(false);
    setIsVotesOpen(false);
    setIsDescriptionExpanded(false);
    setIsDragging(false);
    setDragOffset(0);
    setTouchStartY(0);
  };

  const handleRequestDeleteCurrentAsset = () => {
    if (!canDeleteCurrentAsset || !currentAsset?.id) return;
    setPendingDeleteMediaId(currentAsset.id);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDeleteCurrentAsset = async () => {
    if (!pendingDeleteMediaId) return;
    setIsDeletingMedia(true);

    try {
      await deleteMediaById(pendingDeleteMediaId);
      setUserMedia((prev) => prev.filter((item) => item.id !== pendingDeleteMediaId));
      setIsDeleteDialogOpen(false);
      setPendingDeleteMediaId(null);
      closeSlideshow();
      toast({
        title: t('success'),
        description: t('photoDeletedSuccessfully'),
      });
    } catch (error) {
      console.error('Error deleting media item:', error);
      toast({
        title: t('error'),
        description: t('couldNotDeletePhoto'),
        variant: 'destructive',
      });
    } finally {
      setIsDeletingMedia(false);
    }
  };

  const handleToggleFlagCurrentAsset = async () => {
    if (!currentAsset?.id || !canToggleCurrentAsset || isTogglingFlag) return;

    const itemId = currentAsset.id;
    const currentFlaggedState = Boolean(currentAsset.flagged);
    const newFlaggedState = !currentFlaggedState;
    setIsTogglingFlag(true);

    setUserMedia((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId
          ? { ...item, flagged: newFlaggedState }
          : item,
      ),
    );

    try {
      const { error } = await supabase.rpc('toggle_media_flagged', {
        media_uuid: itemId,
      });

      if (error) throw error;

      toast({
        title: t('success'),
        description: newFlaggedState ? t('photoFlaggedForModeration') : t('photoUnflaggedSuccessfully'),
      });
    } catch (error) {
      console.error('Error toggling flag:', error);

      setUserMedia((prevItems) =>
        prevItems.map((item) =>
          item.id === itemId
            ? { ...item, flagged: currentFlaggedState }
            : item,
        ),
      );

      toast({
        title: t('error'),
        description: t('couldNotChangeModerationStatus'),
        variant: 'destructive',
      });
    } finally {
      setIsTogglingFlag(false);
    }
  };

  const nextSlide = () => {
    if (isTransitioning || usuarioSlideshowAssets.length === 0) return;
    setIsTransitioning(true);
    setPos((p) => p + 1);
    setSlideshowIndex((idx) => (idx + 1) % Math.max(usuarioSlideshowAssets.length, 1));
  };

  const prevSlide = () => {
    if (isTransitioning || usuarioSlideshowAssets.length === 0) return;
    setIsTransitioning(true);
    setPos((p) => p - 1);
    setSlideshowIndex((idx) => (idx - 1 + Math.max(usuarioSlideshowAssets.length, 1)) % Math.max(usuarioSlideshowAssets.length, 1));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const y = e.touches[0].clientY;
    setTouchStartY(y);
    lastYRef.current = y;
    lastTimeRef.current = performance.now();
    velocityRef.current = 0;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const y = e.touches[0].clientY;
    const now = performance.now();
    const dy = lastYRef.current - y;
    const dt = Math.max(1, now - lastTimeRef.current);
    velocityRef.current = dy / dt;

    const diff = touchStartY - y;
    setDragOffset(diff);

    lastYRef.current = y;
    lastTimeRef.current = now;
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const absDist = Math.abs(dragOffset);
    const absVel = Math.abs(velocityRef.current);

    if (!isTransitioning) {
      if (absVel > DRAG_VELOCITY_THRESHOLD || absDist > DRAG_DISTANCE_THRESHOLD_PX) {
        if (dragOffset > 0) {
          nextSlide();
        } else if (dragOffset < 0) {
          prevSlide();
        }
      }
    }

    setDragOffset(0);
    setTouchStartY(0);
    velocityRef.current = 0;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const y = e.clientY;
    setTouchStartY(y);
    lastYRef.current = y;
    lastTimeRef.current = performance.now();
    velocityRef.current = 0;
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const y = e.clientY;
    const now = performance.now();
    const dy = lastYRef.current - y;
    const dt = Math.max(1, now - lastTimeRef.current);
    velocityRef.current = dy / dt;

    const diff = touchStartY - y;
    setDragOffset(diff);

    lastYRef.current = y;
    lastTimeRef.current = now;
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const absDist = Math.abs(dragOffset);
    const absVel = Math.abs(velocityRef.current);

    if (!isTransitioning) {
      if (absVel > DRAG_VELOCITY_THRESHOLD || absDist > DRAG_DISTANCE_THRESHOLD_PX) {
        if (dragOffset > 0) {
          nextSlide();
        } else if (dragOffset < 0) {
          prevSlide();
        }
      }
    }

    setDragOffset(0);
    setTouchStartY(0);
    velocityRef.current = 0;
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (isTransitioning) return;

    if (e.deltaY > 20) {
      nextSlide();
    } else if (e.deltaY < -20) {
      prevSlide();
    }
  };

  useEffect(() => {
    setIsDescriptionExpanded(false);
    setIsCommentsOpen(false);
    setIsVotesOpen(false);
    setIsDeleteDialogOpen(false);
    setPendingDeleteMediaId(null);
  }, [slideshowIndex]);

  useEffect(() => {
    if (!slideshowOpen) return;

    window.history.pushState({ usuarioSlideshow: true }, '');

    const handlePopState = () => {
      popClosingRef.current = true;
      closeSlideshow();
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [slideshowOpen]);

  useEffect(() => {
    if (slideshowOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [slideshowOpen]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!slideshowOpen || isTransitioning) return;

      if (e.key === 'ArrowUp') {
        prevSlide();
      } else if (e.key === 'ArrowDown') {
        nextSlide();
      } else if (e.key === 'Escape') {
        closeSlideshow();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [slideshowOpen, isTransitioning, prevSlide, nextSlide, closeSlideshow]);

  const toggleVote = async (mediaId: string, uploadedBy: string) => {
    if (!user) {
      toast({
        title: 'Inicia sesión',
        description: 'Necesitas iniciar sesión para dar likes en las fotos.',
        variant: 'destructive',
      });
      return;
    }

    if (isBanned) {
      toast({
        title: 'No puedes interactuar',
        description: 'Tu cuenta ha sido suspendida.',
        variant: 'destructive',
      });
      return;
    }

    if (uploadedBy === user.id) {
      toast({
        title: 'Es tu foto',
        description: 'No puedes darle like a tus propias fotos.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { data, error } = await supabase
        .rpc('toggle_media_vote', {
          media_uuid: mediaId,
          user_uuid: user.id,
        });

      if (error) throw error;

      const result = data as { action: string; vote_count: number };

      if (result.action === 'removed') {
        setUserVotes((prev) => {
          const next = new Set(prev);
          next.delete(mediaId);
          return next;
        });
      } else {
        setUserVotes((prev) => new Set([...prev, mediaId]));
      }

      setUserMedia((prev) => prev.map((media) => (
        media.id === mediaId
          ? { ...media, votes_count: result.vote_count }
          : media
      )));
      applyOptimisticVoteState(mediaId, result.action !== 'removed', result.vote_count);
    } catch (error) {
      Sentry.captureMessage(
        error instanceof Error ? error.message : String(error),
        'error'
      );
      console.error('Error toggling like:', error);
      toast({
        title: 'Error',
        description: 'No se pudo procesar tu like. Intenta de nuevo.',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader title={pageTitle} />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-background">
        <PageHeader title={pageTitle} />

        <div className="max-w-4xl mx-auto pb-24">
          {targetUserId && isTargetUserNotFound ? (
            <div className="py-16 flex flex-col items-center justify-center text-center">
              <Ghost className="w-24 h-24 text-muted-foreground/70 mb-4" />
              <p className="text-xl font-medium text-foreground">{t('userNotFound')}</p>
            </div>
          ) : (
            <>
              <section className="relative pt-10 bg-gradient-to-b from-primary via-primary/30 to-transparent">
                <div className="mb-5">
                  {sortedMedia.length > 0 && hasInteractionSortOptions && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="lg"
                          icon="md"
                          className="absolute right-0 top-0 h-14 w-14"
                          aria-label={t('sort')}
                        >
                          <ArrowUpNarrowWide />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[220px]">
                        <DropdownMenuItem
                          className="flex items-center justify-between"
                          onClick={() => setSortBy('recent')}
                        >
                          <span>{t('newest')}</span>
                          {sortBy === 'recent' && <Check className="h-4 w-4" />}
                        </DropdownMenuItem>
                        {canSortByVotes && (
                          <DropdownMenuItem
                            className="flex items-center justify-between"
                            onClick={() => setSortBy('votes')}
                          >
                            <span>{t('mostVotedFirst')}</span>
                            {sortBy === 'votes' && <Check className="h-4 w-4" />}
                          </DropdownMenuItem>
                        )}
                        {canSortByComments && (
                          <DropdownMenuItem
                            className="flex items-center justify-between"
                            onClick={() => setSortBy('comments')}
                          >
                            <span>{t('mostCommentedFirst')}</span>
                            {sortBy === 'comments' && <Check className="h-4 w-4" />}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}

                  <div className="flex flex-col items-center text-center gap-3 px-16 mb-10">
                    <Avatar className="h-28 w-28 border-4 border-background shadow-sm">
                      <AvatarImage src={displayAvatar || undefined} alt={displayName} />
                      <AvatarFallback className="text-2xl font-semibold">{getInitials(displayName)}</AvatarFallback>
                    </Avatar>

                    <h1 className="text-3xl font-semibold leading-tight break-words text-foreground">{displayName}</h1>
                  </div>
                </div>

                <div
                  className={`grid ${statsGridClass} gap-0 rounded-2xl border border-primary/20 bg-primary px-2 mx-4 py-3 text-center mb-5`}
                >
                  {statsItems.map((item, index) => {
                    const Icon = item.icon;
                    const showDivider = index < statsItems.length - 1;

                    return (
                      <div key={item.key} className="relative px-2 py-1">
                        <div className="flex justify-center mb-1">
                          <Icon className="w-5 h-5 text-primary-foreground" strokeWidth={1.8} />
                        </div>
                        <p className="mt-1 text-2xl font-semibold leading-none text-primary-foreground">{item.value}</p>

                        {showDivider && (
                          <div
                            className="absolute right-0 top-2 bottom-2 w-px bg-gradient-to-b from-transparent via-primary-foreground/30 to-transparent"
                            aria-hidden
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>

              <div className="mt-4 ">
                {sortedMedia.length > 0 ? (
                  <MediaGrid
                    items={mediaWithUserInteractions}
                    onTileClick={(item) => openSlideshow(item.id)}
                    selectionMode={false}
                    selectedIds={new Set()}
                    onToggleSelect={() => { }}
                    showComments={isCommentsEnabled}
                    showLikes={isLikesEnabled}
                    gapClassName="gap-0.5"
                    tileClassName="rounded-none border-0"
                  />
                ) : (
                  <div
                    className="rounded-lg bg-card p-6 text-center [color:hsl(var(--surface-muted-fg))]"
                    style={emptyStateStyle}
                  >
                    <p className="mb-4">
                      {targetUserId
                        ? `${targetUserName || 'Este usuario'} no ha subido ninguna foto aún`
                        : 'No has subido ninguna foto aún'}
                    </p>
                    {!targetUserId && isUploadsEnabled && (
                      <Button asChild>
                        <Link to={getAlbumPath('/subir')}>Subir fotos</Link>
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <AlbumSlideshow
        slideshowOpen={slideshowOpen}
        filteredAndSortedAssets={usuarioSlideshowAssets}
        loopedAssets={loopedAssets}
        pos={pos}
        setPos={setPos}
        noTransition={noTransition}
        setNoTransition={setNoTransition}
        isDragging={isDragging}
        dragOffset={dragOffset}
        isCommentsOpen={isCommentsOpen}
        setIsCommentsOpen={setIsCommentsOpen}
        isVotesOpen={isVotesOpen}
        setIsVotesOpen={setIsVotesOpen}
        setIsTransitioning={setIsTransitioning}
        closeSlideshow={closeSlideshow}
        cameFromUsuario={false}
        navigateToUsuario={() => { }}
        getAssetIndexLabel={`${slideshowIndex + 1}/${usuarioSlideshowAssets.length}`}
        slideshowIndex={slideshowIndex}
        isLikesEnabled={isLikesEnabled}
        isCommentsEnabled={isCommentsEnabled}
        isDownloadEnabled={isDownloadEnabled}
        isDescriptionEnabled={isDescriptionEnabled}
        isDescriptionExpanded={isDescriptionExpanded}
        setIsDescriptionExpanded={setIsDescriptionExpanded}
        user={user ? { id: user.id } : null}
        userVotes={userVotes}
        userCommentedMediaIds={userCommentedMediaIds}
        currentLanguage={currentLanguage}
        albumSlug={albumSlug || ''}
        toast={toast as unknown as (...args: unknown[]) => void}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
        onToggleVote={toggleVote}
        showDeleteAction={true}
        canDeleteCurrentAsset={canDeleteCurrentAsset}
        isDeletingCurrentAsset={isDeletingMedia}
        onRequestDeleteCurrentAsset={handleRequestDeleteCurrentAsset}
        showFlagAction={true}
        canToggleCurrentAsset={canToggleCurrentAsset}
        isTogglingFlag={isTogglingFlag}
        onToggleCurrentAssetFlag={handleToggleFlagCurrentAsset}
        showFlaggedBanner={canViewFlaggedMedia}
        onCommentsCountChange={(delta, mediaId) => {
          const id = mediaId || usuarioSlideshowAssets[slideshowIndex]?.id;
          if (!id) return;

          setUserMedia((prev) => prev.map((item) => (
            item.id === id
              ? { ...item, comments_count: Math.max(0, (item.comments_count ?? 0) + delta) }
              : item
          )));
          applyOptimisticCommentDelta(id, delta);

          if (!user) return;

          if (delta > 0) {
            setUserCommentedMediaIds((prev) => new Set(prev).add(id));
            return;
          }

          if (delta < 0) {
            void (async () => {
              const { count } = await supabase
                .from('comments')
                .select('id', { count: 'exact', head: true })
                .eq('media_id', id)
                .eq('user_id', user.id);

              setUserCommentedMediaIds((prev) => {
                const next = new Set(prev);
                if ((count || 0) > 0) {
                  next.add(id);
                } else {
                  next.delete(id);
                }
                return next;
              });
            })();
          }
        }}
        t={t}
      />
      <Dialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          if (isDeletingMedia) return;
          setIsDeleteDialogOpen(open);
        }}
      >
        <DialogContent
          overlayClassName="z-[10009] bg-black/75"
          className="z-[10010] w-[calc(100vw-80px)] max-w-md [&>button]:hidden"
          onPointerDownOutside={(event) => {
            if (isDeletingMedia) {
              event.preventDefault();
            }
          }}
        >
          <DialogHeader className="text-center sm:text-center">
            <DialogTitle>{t('areYouSureTitle')}</DialogTitle>
            <DialogDescription>{t('cannotUndo')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row justify-center gap-3 sm:justify-center sm:space-x-0">
            <Button
              type="button"
              variant="outline"
              className="mt-0 flex-1"
              disabled={isDeletingMedia}
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              {t('cancel')}
            </Button>
            <Button
              type="button"
              className="flex-1"
              disabled={isDeletingMedia}
              onClick={handleConfirmDeleteCurrentAsset}
            >
              {t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Usuario;
